import { z } from 'zod';
import {
  isDistrictInRegion,
  isValidEduid,
  isValidEmail,
  isValidHouseNumber,
  isValidPersonName,
  isValidPsc,
  isValidRodneCislo,
  phoneProblem,
} from './rules';
import { msg } from './messages';

/** Required, trimmed text with a maximum length; whitespace-only values are rejected. */
export function requiredText(max: number, requiredKey: string) {
  return z.string().trim().min(1, { message: requiredKey, abort: true }).max(max, msg('validation.maxLength', { max }));
}

export function optionalText(max: number) {
  return z.string().trim().max(max, msg('validation.maxLength', { max }));
}

const subjectFields = z.object({
  name: requiredText(255, 'subjects.errors.nameRequired'),
  eduid: z.string().trim().min(1, { message: 'subjects.errors.eduidRequired', abort: true }).refine(isValidEduid, 'validation.eduid'),
  type: z.string().min(1, { message: 'subjects.errors.typeRequired', abort: true }),
  street: requiredText(100, 'subjects.errors.streetRequired'),
  houseNumber: z
    .string()
    .trim()
    .min(1, { message: 'subjects.errors.houseNumberRequired', abort: true })
    .max(16, msg('validation.maxLength', { max: 16 }))
    .refine(isValidHouseNumber, 'validation.houseNumber'),
  municipality: requiredText(100, 'subjects.errors.municipalityRequired'),
  postalCode: z.string().trim().min(1, { message: 'subjects.errors.postalCodeRequired', abort: true }).refine(isValidPsc, 'validation.psc'),
  region: z.string().min(1, { message: 'subjects.errors.regionRequired', abort: true }),
  /** Optional; '' = none. */
  district: z.string(),
});

/**
 * The district lies in the region. A separate schema, so its error is reported together with the field errors: a
 * refinement of the object itself does not run while a required field is empty (`abort: true`).
 */
const districtInRegion = z
  .object({ region: z.string(), district: z.string() })
  .refine((v) => isDistrictInRegion(v.district, v.region), { path: ['district'], message: 'validation.districtRegion' });

export const subjectSchema = z.intersection(subjectFields, districtInRegion);

export type SubjectFormValues = z.infer<typeof subjectSchema>;

export const inspectorSchema = z.object({
  titleBefore: optionalText(32),
  firstName: requiredText(32, 'inspectors.errors.firstNameRequired').refine(isValidPersonName, 'validation.personName'),
  lastName: requiredText(64, 'inspectors.errors.lastNameRequired').refine(isValidPersonName, 'validation.personName'),
  titleAfter: optionalText(32),
  personalNumber: z
    .string()
    .trim()
    .min(1, { message: 'inspectors.errors.personalNumberRequired', abort: true })
    // no length limit on the input: "850115 / 1230" is valid and is normalized to 11 characters before saving
    .refine((v) => isValidRodneCislo(v), 'validation.rodneCislo'),
  phone: z
    .string()
    .trim()
    .min(1, { message: 'inspectors.errors.phoneRequired', abort: true })
    .superRefine((v, ctx) => {
      // says what is wrong: the format, the country code, a prefix that does not exist, too short or too long
      const problem = phoneProblem(v);
      if (problem) ctx.addIssue({ code: 'custom', message: msg(problem.key, problem.params) });
    }),
  email: z
    .string()
    .trim()
    .min(1, { message: 'inspectors.errors.emailRequired', abort: true })
    .max(254, msg('validation.maxLength', { max: 254 }))
    .refine(isValidEmail, 'validation.email'),
});

export type InspectorFormValues = z.infer<typeof inspectorSchema>;
